import React, { useCallback, useContext } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import TextLink from '@components/shared/TextLink';
import { NavBarCategory, NavStatus } from '@contracts';
import IconArrow from '@images/vms-account/vms-icons/Arrow.svg';
import { NavContext } from './Context';
import NavBarNewBadge from './NavBarNewBadge';

interface NavLinkProps {
  navLink?: string;
  category?: NavBarCategory;
  internaLinkElements?: JSX.Element[];
  text: string;
  isInternal?: boolean;
  isNew?: boolean;
  color?: string;
}

const NavLink: React.FC<NavLinkProps> = ({ category, navLink, text, internaLinkElements, isInternal, isNew, color }) => {
  const location = useLocation();
  const { navState, navDispatch } = useContext(NavContext);
  const { navbarSelectedCategories, navbarItemColor } = navState;
  const finalLinkColor = color || navbarItemColor || Colors.navyBlue;
  const isExpandable = !!internaLinkElements?.length;
  const isSelectedCategory = category && !!navbarSelectedCategories?.length && navbarSelectedCategories.includes(category);
  const isSelectedLink = !!navLink && location.pathname.includes(navLink);
  const isExternalLink = navLink && navLink.startsWith('http');
  const isSelected = isSelectedCategory || isSelectedLink;
  const linkTargetBlank = isExternalLink ? '_blank' : undefined;
  const linkRel = isExternalLink ? 'noreferrer' : undefined;
  const linkHref = navLink || '#';
  const linkOnClick = useCallback(
    (e: any) => {
      if (category) {
        e.preventDefault();

        if (navbarSelectedCategories.includes(category)) {
          navDispatch({ type: 'removeNavbarSelectedCategory', data: category });
        } else {
          navDispatch({ type: 'removeNavbarSelectedCategories', data: category });
        }
      } else {
        navDispatch({ type: 'setStatus', data: NavStatus.Default });
        navDispatch({ type: 'removeNavbarSelectedCategories' });
      }
    },
    [navbarSelectedCategories]
  );

  return (
    <Wrapper>
      {isInternal ? (
        <InternalLinkWrapper $isSelected={isSelected}>
          <TextLink target={linkTargetBlank} rel={linkRel} $color={finalLinkColor} to={linkHref} onClick={linkOnClick} $noBottomBorder>
            {text}
            <NavBarNewBadge isNew={isNew} />
          </TextLink>
        </InternalLinkWrapper>
      ) : (
        <ExternalLinkWrapper $isSelected={isSelected} $isExpandable={isExpandable}>
          <TextLink target={linkTargetBlank} rel={linkRel} $color={finalLinkColor} to={linkHref} onClick={linkOnClick} $noBottomBorder>
            <TextWrapper>
              {text}
              <NavBarNewBadge isNew={isNew} />
            </TextWrapper>
            {isExpandable ? <IconArrow style={isSelected ? { transform: 'rotate(-180deg)', marginTop: '-3px ' } : null} /> : null}
          </TextLink>
          {internaLinkElements?.length ? (
            <ExternalInnerLinksWrapper $isSelected={isSelected}>{internaLinkElements.map((internalLink) => internalLink)}</ExternalInnerLinksWrapper>
          ) : null}
        </ExternalLinkWrapper>
      )}
    </Wrapper>
  );
};

export default NavLink;

const Wrapper = styled.div``;

const ExternalLinkWrapper = styled.div<{
  $isSelected?: boolean;
  $isExpandable?: boolean;
}>`
  border-bottom: 1px solid ${Colors.ecru};

  ${({ $isSelected }) => ($isSelected ? `padding: 1px 0 16px 0;` : null)}

  ${({ $isExpandable }) => (!$isExpandable ? `padding: 0;` : null)}

  > a {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 15px 0;
    white-space: nowrap;
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: BrownStd;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;

    ${({ $isSelected }) => ($isSelected ? `font-weight: 700;` : null)}

    svg {
      align-self: center;
      transition: transform 0.3s;
      position: relative;
      right: 10px;
    }
  }
`;

const ExternalInnerLinksWrapper = styled.div<{ $isSelected?: boolean }>`
  ${({ $isSelected }) => (!$isSelected ? `display: none; ` : null)}

  > div {
    &:last-child {
      a {
        padding-bottom: 0;
      }
    }
  }
`;

const InternalLinkWrapper = styled.div<{ $isSelected?: boolean }>`
  > a {
    display: flex;
    gap: 4px;
    padding-bottom: 16px;
    white-space: nowrap;
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;

    ${({ $isSelected }) => ($isSelected ? `font-weight: 700; ` : null)}
  }
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
